import { Space, Tooltip } from 'antd';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import RestList from 'containers/rest/List';
import { DynamicObjectType } from 'types/commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import { useDispatch, useSelector } from '@redux';
import { useCallback, useEffect, useMemo } from 'react';
import crudActions from '@redux/crudActions';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { TLayoutItem } from '@redux/layouts/actions';
import FilterLocation from '../components/FilterLocation';
import SummaryCard from '../components/SummaryCard';
import ViewProcessOrdersButton from '../components/ViewProcessOrdersButton';
import dayjs from 'dayjs';
import { exportExcel } from 'api/crud';
import { newCheckRole } from 'utils/tools';

const breadcrumbs = [
  {
    title: 'Inventory',
    href: '#',
  },
  {
    title: 'Inventory',
  },
];

const KEY_FILTER = 'locationCode';

const columns: IRestColumnProps[] = [
  // {
  //   name: 'productSku',
  //   title: 'Product SKU',
  //   isDefault: true,
  //   width: 200,
  // },
  {
    name: 'variantSku',
    title: 'Variant SKU',
    isDefault: true,
    width: 200,
  },
  {
    name: 'location',
    title: 'Location Code',
    render: (dt: DynamicObjectType, rc: DynamicObjectType) => (
      <div style={{ minWidth: '100px' }}>
        <div className="fw-500 text-sm text-active-grey">
          {rc?.locationCode}
        </div>
      </div>
    ),
    isDefault: true,
  },
  {
    name: 'stockOriginal',
    title: (
      <Tooltip title="Stock on hand">
        <Space size={3}>
          ERP stock
          <FontAwesomeIcon icon={faCircleExclamation} />
        </Space>
      </Tooltip>
    ),
    isDefault: true,
    width: 100,
    render: (dt: number) => <div style={{ minWidth: 100 }}>{dt}</div>,
  },
  {
    name: 'stockTotalIMS',
    title: (
      <Tooltip title="Total stock includes related stock">
        <Space size={3}>
          OMS Stock
          <FontAwesomeIcon icon={faCircleExclamation} />
        </Space>
      </Tooltip>
    ),
    isDefault: true,
    render: (dt: number) => <div style={{ minWidth: 120 }}>{dt}</div>,
    width: 120,
  },
  {
    name: 'stockOMS',
    title: 'OMS Original stock',
    isDefault: true,
    render: (dt: number) => <div style={{ minWidth: 150 }}>{dt}</div>,
    width: 150,
  },
  {
    name: 'stockTotalSyncIMS',
    title: 'IMS stock',
    isDefault: true,
    render: (dt: number) => <div style={{ minWidth: 120 }}>{dt}</div>,
    width: 120,
  },
];

const FILTERS_OBJECT = {
  HAS_STOCK: {
    condition: 'and',
    key: 'stockOMS',
    operator: 'gte',
    value: 1,
  },
  OUT_OF_STOCK: {
    condition: 'and',
    key: 'stockOMS',
    operator: 'lte',
    value: 0,
  },
};

const INITIAL_LAYOUTS: TLayoutItem[] = [
  {
    rootPath: '/inventory',
    pathId: 'HAS_STOCK',
    title: 'Has stock',
    customPath: `/inventory?customLayout=HAS_STOCK&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['HAS_STOCK']], null, 0),
    )}`,
    isDefault: true,
  },
  {
    rootPath: '/inventory',
    pathId: 'OUT_OF_STOCK',
    title: 'Out of stock',
    customPath: `/inventory?customLayout=OUT_OF_STOCK&filters=${encodeURIComponent(
      JSON.stringify([FILTERS_OBJECT['OUT_OF_STOCK']], null, 0),
    )}`,
    isDefault: true,
  },
];

export default function List() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { searchParams, filter } = useGetFilter();
  const locationsCurr = useSelector(
    (state) => state.locations.currentData,
  ) as DynamicObjectType;
  const user = useSelector<DynamicObjectType>((state) => state.auth.data);

  const locationId = searchParams.get('locationId');

  const initialFilter = useMemo(
    () => ({
      filter: locationId ? `locationCode||$eq||${locationId}` : undefined,
    }),
    [locationId],
  );

  useEffect(() => {
    if (locationId) {
      dispatch(
        crudActions['locations'].getDataById({
          data: { id: locationId },
          options: {
            suffix: '?join=detail',
            isRequestApi: true,
          },
        }),
      );
    }
  }, []);

  const extraActionsFilter = useMemo(
    () => <FilterLocation keyFilter={KEY_FILTER} />,
    [],
  );

  const handleExcelExport = useCallback(
    (exportExcelQuery, excelName, callback) => {
      exportExcel({
        method: 'POST',
        name: excelName,
        query: exportExcelQuery,
        body: {
          id: filter?.filters?.find((e) => e.key === KEY_FILTER)?.value,
        },
        callback,
      });
    },
    [filter],
  );

  const hasLocationId = useMemo(
    () => !!searchParams.get('locationId'),
    [searchParams],
  );

  const hasExport = useMemo(
    () => !!filter?.filters?.find((e) => e.key === KEY_FILTER)?.value,
    [filter],
  );

  const extraActionsHeader = useMemo(
    () =>
      newCheckRole(
        '/order-item-stocks',
        'GET',
        user?.roles,
        user?.isSuperUser,
      ) && <ViewProcessOrdersButton />,
    [user?.roles, user?.isSuperUser],
  );

  return (
    <>
      {hasLocationId && (
        <LocationTextStyles>
          <div className="fw-600">
            {`${t('mores.locations.titleV2')}:`}&nbsp;
          </div>
          <div className="fw-400">{`${locationsCurr?.name}`}</div>
          <div className="divider-text fw-400" />
          <div className="fw-400">{`${locationsCurr?.id}`}</div>
        </LocationTextStyles>
      )}
      <RestList
        hasOnlyFilter={hasLocationId}
        extraActionFilters={!hasLocationId && extraActionsFilter}
        initialFilter={initialFilter}
        columns={columns}
        breadcrumbs={breadcrumbs}
        hasImport={false}
        model="inventories"
        searchKeys={['variantSku']}
        placeholderInputSearch={t('mores.locations.searchPlaceholderInventory')}
        hasExport={hasExport}
        hasCardComponent={<SummaryCard />}
        initialLayouts={INITIAL_LAYOUTS}
        extraActionsHeader={extraActionsHeader}
        exportExcelQuery={`/export-stocks`}
        excelName={`Export_stocks_${dayjs().format('YYYY_MM_DD')}`}
        customExport={handleExcelExport}
      />
    </>
  );
}

const LocationTextStyles = styled.div`
  margin-bottom: 15px;
  font-size: 16px;
  color: var(--text-title-gray);
  display: flex;
  align-items: center;

  .divider-text {
    width: 2px;
    height: 14px;
    background-color: var(--text-title-gray);
    margin: 0 4px;
  }
`;
